import { useQuery } from '@apollo/client';
import moment from 'moment';
import { FC, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import CLOSE_ICON from '../../assets/images/close.svg';
import { ClipBoardIcon } from '../../assets/images/svgs';
import { AppContext } from '../../context';
import { ROLES } from '../../utils/constant';
import { initials, moneyFormatter } from '../../utils/helper';
import {
  copySheetToClipboard,
  SHEET_FONT_BLUE,
  SHEET_FONT_RED,
} from '../../utils/sheetClipboard';
import Loader from '../layout/Loader';
import { RECRUITER_AGENCY_WEEKLY_ACTIVITY_SHEET } from './gql';

// kan-89: the executive re-keys each imported "Recruiter Agency Weekly Activity"
// report into an external commission Excel. This modal renders every imported
// data row as that sheet's row (columns A–W) and copies them all tab-separated
// so a paste lands each value in its own cell.

interface IWeeklyActivitySheetModalProps {
  /** MetaDataRecruiterAgencyWeeklyActivity id — the listing row's reportMetaId. */
  metaDataId: string;
}

export interface IWeeklyActivitySheetRow {
  id: string;
  companyName?: string | null;
  placementId?: string | null;
  candidateName?: string | null;
  invoiceDate?: string | null;
  invoiceNumber?: string | null;
  periodEndDate?: string | null;
  invoiceAmount?: string | null;
  grossWage?: string | null;
  payType?: string | null;
  units?: string | null;
  payRate?: string | null;
  billRate?: string | null;
  placementJobTitle?: string | null;
  // Candidate's own state, snapshotted onto the matched placement when it was
  // created; null when the candidate record had no address to copy.
  candidateStateCode?: string | null;
  // Job location state saved on the matched placement; null when it had none.
  jobStateCode?: string | null;
  amNames: Array<string>;
  recruiterNames: Array<string>;
  matched: boolean;
}

interface ISheetColumn {
  letter: string;
  name: string;
  // What lands in the clipboard (raw, Excel-friendly).
  value: string;
  // What the modal shows; defaults to `value`.
  display?: string;
  // CSS font color, applied both in the table and in the copied HTML so the
  // two can never drift apart.
  color?: string;
}

/** Unmatched placement numbers show "?" so the executive spots them in the paste. */
const UNMATCHED = '?';

const asText = (value?: string | null): string =>
  value === null || value === undefined ? '' : value;

// Both state codes arrive as nullable strings, so blank or whitespace-only
// values normalize to '' and read as "absent" rather than as a failed CA test.
const asStateCode = (value?: string | null): string =>
  typeof value === 'string' ? value.trim().toUpperCase() : '';

// kan-97: California temps are billed differently, so the executive marks
// their names light blue in the sheet and every other temp name red.
// kan-104: it is where the temp *lives* that decides this, not where the job
// is, so the candidate state snapshotted onto the placement now leads. That
// snapshot (never the live candidate record) keeps an old report reproducing
// the same colours every time it is re-opened. When the placement carries no
// candidate state we fall back to its saved job state, so nothing that is blue
// today silently turns red. An unmatched row has no state at all and stays in
// the red default.
export const isCaliforniaTemp = (row: IWeeklyActivitySheetRow): boolean =>
  !!row?.matched &&
  (asStateCode(row?.candidateStateCode) || asStateCode(row?.jobStateCode)) ===
    'CA';

// Money columns copy the stored string verbatim (already normalized at import,
// e.g. "6400.00") and only display through the currency formatter.
const asMoney = (value?: string | null): ISheetColumn['display'] => {
  const raw = asText(value);
  return raw && !isNaN(Number(raw)) ? moneyFormatter(Number(raw)) : raw;
};

const WeeklyActivitySheetModal: FC<IWeeklyActivitySheetModalProps> = ({
  metaDataId,
}) => {
  const { userRoles } = useContext(AppContext);
  const isExecutive = !!userRoles?.includes(ROLES.EXECUTIVE);

  const [open, setOpen] = useState<boolean>(false);

  const { data, loading, error } = useQuery<{
    recruiterAgencyWeeklyActivitySheet: { rows: Array<IWeeklyActivitySheetRow> };
  }>(RECRUITER_AGENCY_WEEKLY_ACTIVITY_SHEET, {
    variables: { metaDataId },
    // The sheet is only needed once the executive opens the modal.
    skip: !open || !metaDataId,
    fetchPolicy: 'network-only',
  });

  if (!isExecutive || !metaDataId) return null;

  const toggle = () => setOpen(!open);

  const rows = data?.recruiterAgencyWeeklyActivitySheet?.rows || [];

  // A split whose user is missing (deactivated) arrives as "?" rather than
  // silently under-reporting the side.
  const sideInitials = (names: Array<string>): string =>
    (names || []).map((name) => initials(name) || UNMATCHED).join(' | ');

  const rowToColumns = (row: IWeeklyActivitySheetRow): Array<ISheetColumn> => {
    const invoiceDate = moment(asText(row?.invoiceDate), 'MM/DD/YYYY');
    // Every pasted cell is red; only the temp's name flips to light blue when
    // the temp is a California one.
    const nameColor = isCaliforniaTemp(row) ? SHEET_FONT_BLUE : SHEET_FONT_RED;

    return [
      { letter: 'A', name: "Chk Rec'd Date", value: '' },
      { letter: 'B', name: 'Check Number', value: '' },
      { letter: 'C', name: 'Check Amt', value: '' },
      { letter: 'D', name: 'Applied to inv', value: '' },
      {
        letter: 'E',
        name: 'Invoice Due Date',
        // Net-30 off the invoice date; blank when the import had no usable date.
        value: invoiceDate.isValid()
          ? invoiceDate.add(30, 'days').format('MM/DD/YYYY')
          : '',
      },
      { letter: 'F', name: 'Aging', value: '' },
      {
        letter: 'G',
        name: 'AM Source',
        value: row?.matched ? sideInitials(row?.amNames) : UNMATCHED,
      },
      {
        letter: 'H',
        name: 'Rec Source',
        value: row?.matched ? sideInitials(row?.recruiterNames) : UNMATCHED,
      },
      {
        letter: 'I',
        name: 'Temp Position',
        value: row?.matched ? asText(row?.placementJobTitle) : UNMATCHED,
      },
      { letter: 'J', name: 'Placed Id', value: asText(row?.placementId) },
      { letter: 'K', name: 'Client', value: asText(row?.companyName) },
      {
        letter: 'L',
        name: 'Temp Employee',
        value: asText(row?.candidateName),
        color: nameColor,
      },
      { letter: 'M', name: 'Invoice Date', value: asText(row?.invoiceDate) },
      { letter: 'N', name: 'Invoice Number', value: asText(row?.invoiceNumber) },
      { letter: 'O', name: 'Week Ending', value: asText(row?.periodEndDate) },
      {
        letter: 'P',
        name: 'Invoice Amount',
        value: asText(row?.invoiceAmount),
        display: asMoney(row?.invoiceAmount),
      },
      { letter: 'Q', name: 'Invoice Adjmnt', value: '' },
      { letter: 'R', name: 'Invoice A Sales Tax', value: '' },
      {
        letter: 'S',
        name: 'Gross Wages',
        value: asText(row?.grossWage),
        display: asMoney(row?.grossWage),
      },
      { letter: 'T', name: 'Pay Type', value: asText(row?.payType) },
      { letter: 'U', name: 'Units', value: asText(row?.units) },
      {
        letter: 'V',
        name: 'Employee Pay Rate',
        value: asText(row?.payRate),
        display: asMoney(row?.payRate),
      },
      {
        letter: 'W',
        name: 'Client Bill Rate',
        value: asText(row?.billRate),
        display: asMoney(row?.billRate),
      },
    ].map((column) => ({ color: SHEET_FONT_RED, ...column }));
  };

  // Header letters/labels are the same for every row.
  const headerColumns: Array<ISheetColumn> = rowToColumns({
    id: '',
    amNames: [],
    recruiterNames: [],
    matched: false,
  });

  const copyAll = async () => {
    // Copies an HTML flavour alongside the tab-separated text so the light-blue
    // California names survive the paste into Excel.
    const cells = rows.map((row) =>
      rowToColumns(row).map((column) => ({
        text: column.value,
        color: column.color,
      }))
    );
    const copied = await copySheetToClipboard(cells);
    if (copied) {
      toast.success(`${rows.length} rows copied — paste into the sheet`);
    } else {
      toast.error('Copy failed — please copy the values manually');
    }
  };

  return (
    <>
      <Button
        type="button"
        title="Commission Sheet"
        className="bg-transparent border-0 p-0 mr-3"
        onClick={toggle}
      >
        <span className="d-flex"><ClipBoardIcon /></span>
      </Button>

      <Modal
        isOpen={open}
        toggle={toggle}
        size="xl"
        className="revampDialog"
        style={{ maxWidth: '95vw' }}
      >
        <ModalHeader toggle={toggle}>
          <div className="d-flex align-items-center">
            <span>Temp Commission Sheet Rows</span>
          </div>
          <button className="iconButton" onClick={toggle} type="button">
            <img src={CLOSE_ICON} alt="close" />
          </button>
        </ModalHeader>

        <ModalBody>
          {loading && <Loader loaderClass="sm-height" />}

          {!loading && error && (
            <p className="text-danger m-0">
              {error?.message || 'Unable to load the commission sheet rows'}
            </p>
          )}

          {!loading && !error && !rows.length && (
            <p className="text-muted m-0">No rows found for this report</p>
          )}

          {!loading && !error && !!rows.length && (
            // One row per imported line, like the sheet itself, scrollable sideways.
            <div style={{ overflowX: 'auto' }}>
              <Table
                bordered
                size="sm"
                style={{ width: 'max-content', fontSize: '12px' }}
              >
                <thead>
                  <tr>
                    {headerColumns.map((column) => (
                      <th
                        key={column.letter}
                        style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}
                      >
                        <small className="text-muted d-block font-weight-normal">
                          {column.letter}
                        </small>
                        {column.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row?.id}>
                      {rowToColumns(row).map((column) => {
                        const shown = column.display ?? column.value;
                        return (
                          <td
                            key={column.letter}
                            style={{
                              whiteSpace: 'nowrap',
                              // Preview matches what the paste will look like.
                              color: shown ? column.color : undefined,
                            }}
                          >
                            {shown || <span className="text-muted">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <button type="button" onClick={toggle} className="buttonPrimary mr-3">
            Close
          </button>
          <button
            type="button"
            onClick={copyAll}
            disabled={!rows.length}
            className="buttonGenericStyle filledButton"
          >
            Copy All
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default WeeklyActivitySheetModal;
